import React from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as TrashSvg } from '../../image/Trash.svg';

export default function ProvisionSelectPanel(props) {
  const {
    provisions,
    provisionKeys,
    onToggle,
    onClickDelete,
    onClickAdd
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const translateTitle = (title) => {
    const titlesObj = t('myStoreSetting.terms.provisionTitles');
    if (typeof titlesObj === 'object' && titlesObj[title]) return titlesObj[title];
    return title;
  };

  // 삭제 버튼 클릭 — 토글 이벤트 차단 //
  function onClickDeleteToggle(e, data) {
    e.stopPropagation();
    if (onClickDelete) onClickDelete(data);
  }

  // 개별 항목 토글 렌더 //
  function renderProvisionToggle(data) {
    const isOn = provisionKeys.includes(data.id);

    return (
      <ItemWrapper key={'ProvisionToggle-' + data.id} onClick={() => onToggle(data)}>
        <LabelWrapper>
          <LabelText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{translateTitle(data.title)}</LabelText>
        </LabelWrapper>
        <RightGroup>
          <ToggleBox isOn={isOn}>
            <ToggleKnob isOn={isOn} />
          </ToggleBox>
          {
            onClickDelete &&
            <DeleteButton onClick={(e) => onClickDeleteToggle(e, data)}>
              <TrashIcon />
            </DeleteButton>
          }
        </RightGroup>
      </ItemWrapper>
    );
  }

  // 항목 토글 리스트 렌더 //
  function renderProvisionToggles() {
    if (provisions.length === 0) {
      return (
        <EmptyHintWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={13} color={theme.subText}>{t('myStoreSetting.terms.provisionContentPlaceholder')}</Text>
        </EmptyHintWrapper>
      );
    }

    return provisions.map((data) => renderProvisionToggle(data));
  }

  return (
    <PanelWrapper>
      <HeaderRow>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('myStoreSetting.terms.itemsToDisplay')}</Text>
      </HeaderRow>

      <DivisionLine backgroundColor={theme.line} />

      <ToggleListScroll>
        {renderProvisionToggles()}
      </ToggleListScroll>

      <AddProvisionButton onClick={onClickAdd}>
        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.primary}>+ {t('myStoreSetting.terms.addProvision')}</Text>
      </AddProvisionButton>
    </PanelWrapper>
  );
}

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 20px 10px 20px 15px;

  width: 243px;

  gap: 10px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  box-sizing: border-box;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const LabelText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const EmptyHintWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 20px 0;

  width: 100%;
`;

const DivisionLine = styled.div`
  width: 100%;
  height: 2px;

  background-color: ${props => props.backgroundColor};

  opacity: 0.3;
`;

const ToggleListScroll = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  flex: 1 0 0;
  min-height: 0;

  gap: 10px;

  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 1px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.primary};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  height: 32px;

  margin-right: 9px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LabelWrapper = styled.div`
  flex: 1 0 0;
  min-width: 0;
`;

const RightGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;
`;

const DeleteButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  & path {
    fill: ${props => props.theme.line};

    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }

    &:hover path {
      fill: ${props => props.theme.pointRed};
    }
  }
`;

const TrashIcon = styled(TrashSvg)`
  width: 20px;
  height: 20px;
`;

const ToggleBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 68px;
  height: 32px;

  border: 1px solid ${props => props.isOn ? `transparent` : props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.isOn ? props.theme.primary : props.theme.subBackground};

  box-sizing: border-box;

  transition: all 0.3s ease-in-out;
`;

const ToggleKnob = styled.div`
  margin-left: ${props => props.isOn ? `38px` : `3px`};

  width: 25px;
  height: 25px;

  border: 1px solid ${props => props.isOn ? props.theme.primary : `transparent`};
  border-radius: 8px;
  background-color: ${props => props.isOn ? props.theme.white : props.theme.line};

  transition: all 0.3s ease-in-out;
`;

const AddProvisionButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px 0;

  width: 100%;

  border: 1px dashed ${props => props.theme.primary};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  box-sizing: border-box;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;
